/**
 * Which locale, and how it changes. Decided BEFORE the first screen renders
 * (boot.ts), remembered in the global state, and switchable from the pre-auth
 * page onwards (LocaleSwitch.vue).
 */
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { i18n } from "./index";
import { DEFAULT_LOCALE, LOCALE_NAMES, SUPPORTED_LOCALES, isShellLocale, matchLocale, type ShellLocale } from "./locales";

export interface LocaleSources {
  /** `?locale=af-ZA` on the URL — a shareable link, and the dev/proof override. */
  search?: string;
  /** What the visitor chose last time (global state). */
  persisted?: string | null;
  /** `navigator.languages`, in the visitor's order of preference. */
  navigatorLanguages?: readonly string[];
}

/** The slice of a vue-i18n global composer the locale logic touches. */
export interface LocaleRuntime {
  locale: { value: string };
}

/** `?locale=` → remembered choice → the browser's preferences → the default. */
export function detectLocale(sources: LocaleSources): ShellLocale {
  const fromQuery = matchLocale(new URLSearchParams(sources.search ?? "").get("locale"));
  if (fromQuery) return fromQuery;
  const remembered = matchLocale(sources.persisted);
  if (remembered) return remembered;
  for (const tag of sources.navigatorLanguages ?? []) {
    const hit = matchLocale(tag);
    if (hit) return hit;
  }
  return DEFAULT_LOCALE;
}

/** Make `locale` the active one: the runtime, `<html lang>`, and the host state modules read. */
export function applyLocale(locale: ShellLocale, runtime: LocaleRuntime = i18n.global, doc: Document = document): void {
  runtime.locale.value = locale;
  doc.documentElement.lang = locale;
  const globalState = window.PinksyGlobalState;
  if (globalState && globalState.getState().locale !== locale) globalState.updateState({ locale });
}

/**
 * The component-side view: the active locale, the list, and the switch.
 * Reads the runtime the app INSTALLED (`useI18n`, global scope) — inside
 * `setup()` only — unless a runtime is handed in.
 */
export function useShellLocale(runtime: LocaleRuntime = useI18n({ useScope: "global" })) {
  const locale = computed<ShellLocale>(() => {
    const active = runtime.locale.value;
    return isShellLocale(active) ? active : DEFAULT_LOCALE;
  });
  return {
    locale,
    locales: SUPPORTED_LOCALES,
    names: LOCALE_NAMES,
    setLocale: (next: ShellLocale) => applyLocale(next, runtime),
  };
}
